// ── BCH Live — Chat ───────────────────────────────────────────────
//
// NIP-53 live chat (kind 1311) scoped to a stream's `a` coordinate,
// with the interaction layer a modern live chat needs:
//
//   threaded replies · emoji reactions (kind 7) · creator + moderator
//   badges · pinned messages · @mention highlighting · auto-scroll
//   toggle · rich link previews · relative + absolute timestamps ·
//   profile hover cards · BCH tipping on every message
//
// Performance: the visible window is capped (windowing rather than a
// full virtualiser, which keeps variable-height messages correct while
// still bounding DOM nodes), and rows are memoised so a new message
// doesn't re-render the backlog.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Send, MessageCircle, User, ArrowDown, Pin, PinOff, Reply, Smile,
  Shield, Radio, X, ExternalLink,
} from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../../hooks/useNostrQuery';
import { useNostrPublish } from '../../hooks/useNostrPublish';
import { LIVE_CHAT_KIND, slog, swarn } from '../../lib/streamCredentials';
import { timeAgo } from '../../lib/utils';
import BCHTipButton from '../BCHTipButton';
import VerifiedBadge from '../VerifiedBadge';
import type { Event } from 'nostr-tools';

const REACTION_KIND = 7;
const QUICK_REACTIONS = ['💚', '🔥', '👏', '😂', '🚀', '🙌'];

/** Cap on rendered rows. Older messages stay queryable but leave the DOM. */
const MAX_RENDERED = 120;

// ══════════════════════════════════════════════════════════════════
//  URL / MENTION PARSING
// ══════════════════════════════════════════════════════════════════

const URL_RE = /(https?:\/\/[^\s<>"']+)/gi;
const MENTION_RE = /\B@([a-z0-9_.-]{2,40})\b/gi;

/** https-only allowlist. Never trust an event-sourced URL. */
function safeUrl(raw: string): string | null {
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    return u.href;
  } catch {
    return null;
  }
}

function isImageUrl(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|avif)(\?|$)/i.test(url);
}

/**
 * Render message text with linkified URLs and highlighted mentions.
 * Deliberately builds React nodes — never innerHTML — because chat
 * content is fully attacker-controlled.
 */
function renderContent(text: string, myName: string | null): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let key = 0;

  for (const segment of text.split(URL_RE)) {
    if (!segment) continue;

    if (/^https?:\/\//i.test(segment)) {
      const href = safeUrl(segment);
      if (href) {
        out.push(
          <a
            key={key++}
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={e => e.stopPropagation()}
            className="break-all text-primary underline decoration-primary/40 hover:decoration-primary"
          >
            {segment.length > 48 ? `${segment.slice(0, 45)}…` : segment}
          </a>,
        );
        continue;
      }
    }

    // Mentions inside the non-URL segments
    let last = 0;
    segment.replace(MENTION_RE, (match, handle: string, offset: number) => {
      if (offset > last) out.push(<span key={key++}>{segment.slice(last, offset)}</span>);
      const isMe = !!myName && handle.toLowerCase() === myName.toLowerCase();
      out.push(
        <span
          key={key++}
          className={isMe
            ? 'rounded bg-primary/25 px-1 font-bold text-primary'
            : 'font-semibold text-primary/80'}
        >
          {match}
        </span>,
      );
      last = offset + match.length;
      return match;
    });
    if (last < segment.length) out.push(<span key={key++}>{segment.slice(last)}</span>);
  }

  return out;
}

/** First safe link in a message, for the preview card. */
function firstLink(text: string): string | null {
  const m = text.match(URL_RE);
  if (!m) return null;
  return safeUrl(m[0]);
}

// ══════════════════════════════════════════════════════════════════
//  LINK PREVIEW
// ══════════════════════════════════════════════════════════════════

function LinkPreview({ url }: { url: string }) {
  let host = '';
  try { host = new URL(url).hostname.replace(/^www\./, ''); } catch { return null; }

  if (isImageUrl(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer nofollow" onClick={e => e.stopPropagation()} className="mt-1 block">
        <img
          src={url}
          alt=""
          loading="lazy"
          className="max-h-40 w-auto rounded-lg border border-border object-cover"
        />
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onClick={e => e.stopPropagation()}
      className="mt-1 flex items-center gap-1.5 rounded-lg border border-border bg-muted/50 px-2 py-1.5 transition-colors hover:bg-muted"
    >
      <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate text-[11px] font-medium text-muted-foreground">{host}</span>
    </a>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PROFILE HOVER CARD
// ══════════════════════════════════════════════════════════════════

function HoverCard({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  return (
    <div className="w-56 overflow-hidden rounded-xl border border-border bg-popover shadow-2xl">
      <div className="h-12 bg-gradient-to-r from-primary/25 to-emerald-500/20">
        {profile?.banner && <img src={profile.banner} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="-mt-5 px-3 pb-3">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="h-10 w-10 rounded-full object-cover ring-2 ring-popover" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 ring-2 ring-popover">
            <User className="h-5 w-5 text-primary/60" />
          </div>
        )}
        <p className="mt-1.5 flex items-center gap-1 truncate text-sm font-bold">
          <span className="truncate">
            {profile?.display_name || profile?.name || pubkey.slice(0, 10)}
          </span>
          <VerifiedBadge nip05={profile?.nip05} pubkey={pubkey} size="xs" />
        </p>
        {profile?.nip05 && <p className="truncate text-[11px] text-primary/70">{profile.nip05}</p>}
        {profile?.about && (
          <p className="mt-1 line-clamp-3 text-[11px] leading-snug text-muted-foreground">{profile.about}</p>
        )}
        <Link
          to={`/profile/${pubkey}`}
          className="mt-2 block rounded-lg bg-muted py-1.5 text-center text-[11px] font-bold hover:bg-accent"
        >
          View profile
        </Link>
      </div>
    </div>
  );
}

function NameWithHover({
  pubkey, className,
}: { pubkey: string; className?: string }) {
  const { profile } = useNostrProfile(pubkey);
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);

  const show = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), 400);
  };
  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setOpen(false);
  };
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  return (
    <span className="relative inline-flex items-center gap-0.5" onMouseEnter={show} onMouseLeave={hide}>
      <Link to={`/profile/${pubkey}`} className={className}>
        {profile?.display_name || profile?.name || pubkey.slice(0, 8)}
      </Link>
      {/* Paid BCHNostr verification, confirmed against the NIP-05 directory */}
      <VerifiedBadge nip05={profile?.nip05} pubkey={pubkey} size="xs" />
      {open && (
        <span className="absolute bottom-full left-0 z-50 mb-1 hidden sm:block">
          <HoverCard pubkey={pubkey} />
        </span>
      )}
    </span>
  );
}

// ── Small avatar ──────────────────────────────────────────────────
function ChatAvatar({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  return profile?.picture
    ? <img src={profile.picture} alt="" loading="lazy" className="h-5 w-5 rounded-full object-cover" />
    : <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10"><User className="h-3 w-3 text-primary/60" /></div>;
}

// ── Role badges ───────────────────────────────────────────────────
function RoleBadge({ role }: { role: 'creator' | 'moderator' }) {
  if (role === 'creator') {
    return (
      <span
        title="Stream creator"
        className="inline-flex shrink-0 items-center gap-0.5 rounded bg-red-600 px-1 py-px text-[9px] font-black uppercase text-white"
      >
        <Radio className="h-2.5 w-2.5" /> Host
      </span>
    );
  }
  return (
    <span
      title="Moderator"
      className="inline-flex shrink-0 items-center gap-0.5 rounded bg-blue-600 px-1 py-px text-[9px] font-black uppercase text-white"
    >
      <Shield className="h-2.5 w-2.5" /> Mod
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════
//  MESSAGE ROW
// ══════════════════════════════════════════════════════════════════

interface RowProps {
  event: Event;
  role: 'creator' | 'moderator' | null;
  myName: string | null;
  mentionsMe: boolean;
  reactions: Map<string, number>;
  parent: Event | null;
  canModerate: boolean;
  isPinned: boolean;
  onReply: (ev: Event) => void;
  onReact: (id: string, emoji: string) => void;
  onPin: (ev: Event | null) => void;
  showTip: boolean;
}

const MessageRow = React.memo(function MessageRow({
  event, role, myName, mentionsMe, reactions, parent,
  canModerate, isPinned, onReply, onReact, onPin, showTip,
}: RowProps) {
  const { profile } = useNostrProfile(event.pubkey);
  const [picker, setPicker] = useState(false);
  const link = useMemo(() => firstLink(event.content), [event.content]);
  const body = useMemo(() => renderContent(event.content, myName), [event.content, myName]);
  const absolute = useMemo(
    () => new Date(event.created_at * 1000).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
    [event.created_at],
  );

  return (
    <div
      className={`group/msg relative rounded-lg px-1.5 py-1 transition-colors hover:bg-muted/40 ${
        mentionsMe ? 'bg-primary/10 ring-1 ring-inset ring-primary/25' : ''
      }`}
    >
      {/* Quoted parent for threaded replies */}
      {parent && (
        <div className="mb-0.5 flex items-center gap-1 border-l-2 border-border pl-1.5 text-[10px] text-muted-foreground">
          <Reply className="h-2.5 w-2.5 shrink-0" />
          <span className="shrink-0 font-semibold">
            {/* Name only — avoids a nested hover card inside the quote */}
            <NameInlineStatic pubkey={parent.pubkey} />
          </span>
          <span className="truncate opacity-80">{parent.content}</span>
        </div>
      )}

      <div className="flex items-start gap-1.5">
        <Link to={`/profile/${event.pubkey}`} className="mt-0.5 shrink-0">
          <ChatAvatar pubkey={event.pubkey} />
        </Link>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1">
            <NameWithHover
              pubkey={event.pubkey}
              className="text-[11px] font-bold hover:text-primary"
            />
            {role && <RoleBadge role={role} />}
            <span className="text-[10px] text-muted-foreground" title={absolute}>
              {timeAgo(event.created_at)}
            </span>
          </div>

          <p className="break-words text-xs leading-snug text-foreground/90">{body}</p>

          {link && <LinkPreview url={link} />}

          {/* Reaction pills */}
          {reactions.size > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {[...reactions.entries()].map(([emoji, count]) => (
                <button
                  key={emoji}
                  onClick={() => onReact(event.id, emoji)}
                  className="flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-px text-[10px] font-bold transition-colors hover:bg-accent"
                >
                  <span>{emoji}</span>
                  <span className="text-muted-foreground">{count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Hover actions */}
        <div className="absolute right-1 top-0.5 flex items-center gap-0.5 rounded-lg border border-border bg-popover p-0.5 opacity-0 shadow-sm transition-opacity group-hover/msg:opacity-100 focus-within:opacity-100">
          <button
            onClick={() => setPicker(p => !p)}
            title="React"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Smile className="h-3 w-3" />
          </button>
          <button
            onClick={() => onReply(event)}
            title="Reply"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Reply className="h-3 w-3" />
          </button>
          {showTip && (
            <span className="no-navigate" onClick={e => e.stopPropagation()}>
              <BCHTipButton
                recipientPubkey={event.pubkey}
                recipientProfile={profile}
                dropdownAlign="right"
              />
            </span>
          )}
          {canModerate && (
            <button
              onClick={() => onPin(isPinned ? null : event)}
              title={isPinned ? 'Unpin' : 'Pin message'}
              className={`rounded p-1 hover:bg-muted ${isPinned ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {isPinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
            </button>
          )}
        </div>

        {/* Emoji picker */}
        {picker && (
          <div className="absolute right-1 top-8 z-30 flex gap-0.5 rounded-xl border border-border bg-popover p-1 shadow-xl">
            {QUICK_REACTIONS.map(e => (
              <button
                key={e}
                onClick={() => { onReact(event.id, e); setPicker(false); }}
                className="rounded-lg px-1.5 py-1 text-base transition-transform hover:scale-125 hover:bg-muted"
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

/** Name without hover card — used inside quoted replies. */
function NameInlineStatic({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  return <>{profile?.display_name || profile?.name || pubkey.slice(0, 8)}</>;
}

// ══════════════════════════════════════════════════════════════════
//  PANEL
// ══════════════════════════════════════════════════════════════════

export interface LiveChatPanelProps {
  aTag: string;
  /** Stream host — gets the creator badge and pin rights */
  creatorPubkey: string;
  /** Additional moderator pubkeys */
  moderators?: string[];
  className?: string;
}

export default function LiveChatPanel({
  aTag, creatorPubkey, moderators = [], className = '',
}: LiveChatPanelProps) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile: myProfile } = useNostrProfile(user?.pubkey);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState<Event | null>(null);
  const [pinned, setPinned] = useState<Event | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [unseen, setUnseen] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);
  const initRef = useRef(true);

  const since = useMemo(() => Math.floor(Date.now() / 1000) - 86400, []);

  // Chat + reactions in one query set, both relay-filtered by `a`.
  const { events, loading, refetch } = useNostrQuery({
    filters: aTag
      ? [
          { kinds: [LIVE_CHAT_KIND], '#a': [aTag], limit: 200, since },
          { kinds: [REACTION_KIND], '#a': [aTag], limit: 300, since },
        ]
      : [],
    enabled: !!aTag,
  });

  useEffect(() => { if (aTag) slog('chat', `bound to room a=${aTag}`); }, [aTag]);

  const { messages, reactionsByTarget } = useMemo(() => {
    const msgs: Event[] = [];
    const reactions = new Map<string, Map<string, number>>();

    for (const ev of events) {
      if (ev.kind === LIVE_CHAT_KIND) {
        msgs.push(ev);
      } else if (ev.kind === REACTION_KIND) {
        // Reaction targets the message id via its last `e` tag.
        const target = ev.tags.filter(t => t[0] === 'e').pop()?.[1];
        if (!target) continue;
        const emoji = (ev.content || '+').trim() || '+';
        const norm = emoji === '+' ? '💚' : emoji;
        let m = reactions.get(target);
        if (!m) { m = new Map(); reactions.set(target, m); }
        m.set(norm, (m.get(norm) || 0) + 1);
      }
    }

    msgs.sort((a, b) => a.created_at - b.created_at);
    // Bound the DOM: keep the most recent window.
    const windowed = msgs.length > MAX_RENDERED ? msgs.slice(-MAX_RENDERED) : msgs;
    return { messages: windowed, reactionsByTarget: reactions };
  }, [events]);

  const byId = useMemo(() => {
    const m = new Map<string, Event>();
    for (const ev of messages) m.set(ev.id, ev);
    return m;
  }, [messages]);

  const modSet = useMemo(() => new Set(moderators), [moderators]);
  const myName = myProfile?.name || myProfile?.display_name || null;
  const canModerate = !!user && (user.pubkey === creatorPubkey || modSet.has(user.pubkey));

  const roleOf = useCallback((pk: string): 'creator' | 'moderator' | null => {
    if (pk === creatorPubkey) return 'creator';
    if (modSet.has(pk)) return 'moderator';
    return null;
  }, [creatorPubkey, modSet]);

  // ── Scrolling ──
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // Track whether the user has drifted away from the bottom. Auto-scroll is
  // a user-controlled toggle, but scrolling up implicitly pauses it so chat
  // never yanks the view while someone is reading back.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (dist > 80 && autoScroll) setAutoScroll(false);
      if (dist <= 8 && !autoScroll) { setAutoScroll(true); setUnseen(0); }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [autoScroll]);

  useEffect(() => {
    const c = messages.length;
    if (initRef.current) {
      if (!loading && c > 0) {
        initRef.current = false;
        prevCountRef.current = c;
        requestAnimationFrame(() => scrollToBottom('instant' as ScrollBehavior));
      }
      return;
    }
    if (c <= prevCountRef.current) { prevCountRef.current = c; return; }
    const added = c - prevCountRef.current;
    prevCountRef.current = c;
    if (autoScroll) requestAnimationFrame(() => scrollToBottom('smooth'));
    else setUnseen(n => n + added);
  }, [messages.length, loading, autoScroll, scrollToBottom]);

  // ── Actions ──
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !user || sending) return;
    setSending(true);
    try {
      const tags: string[][] = [['a', aTag, '', 'root']];
      // Threaded reply: mark the parent and notify its author.
      if (replyTo) {
        tags.push(['e', replyTo.id, '', 'reply']);
        tags.push(['p', replyTo.pubkey]);
      }
      await publish({
        kind: LIVE_CHAT_KIND,
        content: text,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      setDraft('');
      setReplyTo(null);
      setAutoScroll(true);
      setUnseen(0);
      setTimeout(() => scrollToBottom('smooth'), 200);
      setTimeout(refetch, 1000);
    } catch (err) {
      swarn('chat', 'failed to publish message', err);
    } finally {
      setSending(false);
    }
  };

  const handleReact = useCallback(async (msgId: string, emoji: string) => {
    if (!user) return;
    const target = byId.get(msgId);
    try {
      await publish({
        kind: REACTION_KIND,
        content: emoji,
        tags: [
          ['e', msgId],
          ['a', aTag],
          ...(target ? [['p', target.pubkey]] : []),
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setTimeout(refetch, 800);
    } catch (err) {
      swarn('chat', 'failed to publish reaction', err);
    }
  }, [user, publish, aTag, refetch, byId]);

  const parentOf = useCallback((ev: Event): Event | null => {
    const replyId = ev.tags.find(t => t[0] === 'e' && t[3] === 'reply')?.[1]
      || ev.tags.find(t => t[0] === 'e')?.[1];
    if (!replyId) return null;
    return byId.get(replyId) || null;
  }, [byId]);

  return (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card ${className}`}>
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <h3 className="flex items-center gap-1.5 text-sm font-bold">
          <MessageCircle className="h-4 w-4 text-primary" /> Live Chat
        </h3>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold text-muted-foreground">{messages.length}</span>
          <button
            onClick={() => {
              const next = !autoScroll;
              setAutoScroll(next);
              if (next) { setUnseen(0); scrollToBottom('smooth'); }
            }}
            title={autoScroll ? 'Auto-scroll on' : 'Auto-scroll paused'}
            className={`rounded-md px-1.5 py-1 text-[10px] font-bold transition-colors ${
              autoScroll
                ? 'bg-primary/15 text-primary'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            Auto
          </button>
        </div>
      </div>

      {/* Pinned message */}
      {pinned && (
        <div className="flex shrink-0 items-start gap-1.5 border-b border-primary/25 bg-primary/10 px-3 py-1.5">
          <Pin className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-primary/70">Pinned</p>
            <p className="truncate text-xs">
              <span className="font-bold"><NameInlineStatic pubkey={pinned.pubkey} /></span>
              <span className="ml-1 text-foreground/85">{pinned.content}</span>
            </p>
          </div>
          {canModerate && (
            <button onClick={() => setPinned(null)} className="shrink-0 rounded p-0.5 text-primary/60 hover:text-primary">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          className="live-chat-scroll h-full overflow-y-auto px-1.5 py-1"
          style={{ contain: 'content' }}
        >
          {loading && messages.length === 0 ? (
            <div className="space-y-2 p-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <div className="h-5 w-5 shrink-0 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-1">
                    <div className="h-2.5 w-24 animate-pulse rounded bg-muted" />
                    <div className="h-2.5 w-4/5 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
              <MessageCircle className="h-8 w-8 text-muted-foreground/25" />
              <p className="text-xs font-semibold text-muted-foreground">No messages yet</p>
              <p className="text-[11px] text-muted-foreground/70">Say hello — or send a Super Chat to stand out.</p>
            </div>
          ) : (
            messages.map(ev => (
              <MessageRow
                key={ev.id}
                event={ev}
                role={roleOf(ev.pubkey)}
                myName={myName}
                mentionsMe={!!myName && new RegExp(`@${myName}\\b`, 'i').test(ev.content)}
                reactions={reactionsByTarget.get(ev.id) || new Map()}
                parent={parentOf(ev)}
                canModerate={canModerate}
                isPinned={pinned?.id === ev.id}
                onReply={setReplyTo}
                onReact={handleReact}
                onPin={setPinned}
                showTip={!!user && ev.pubkey !== user.pubkey}
              />
            ))
          )}
        </div>

        {/* Jump-to-latest */}
        {!autoScroll && (
          <button
            onClick={() => { setAutoScroll(true); setUnseen(0); scrollToBottom('smooth'); }}
            className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-[11px] font-bold text-primary-foreground shadow-lg transition-transform hover:scale-105"
          >
            <ArrowDown className="h-3 w-3" />
            {unseen > 0 ? `${unseen} new message${unseen === 1 ? '' : 's'}` : 'Jump to latest'}
          </button>
        )}
      </div>

      {/* Reply context */}
      {replyTo && (
        <div className="flex shrink-0 items-center gap-1.5 border-t border-border bg-muted/40 px-3 py-1.5">
          <Reply className="h-3 w-3 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
            Replying to <span className="font-bold text-foreground"><NameInlineStatic pubkey={replyTo.pubkey} /></span>
          </span>
          <button onClick={() => setReplyTo(null)} className="shrink-0 rounded p-0.5 hover:bg-muted">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Composer */}
      {user ? (
        <form onSubmit={handleSend} className="flex shrink-0 items-center gap-2 border-t border-border bg-background px-2 py-2">
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={replyTo ? 'Write a reply…' : 'Say something…'}
            maxLength={280}
            disabled={sending}
            className="min-w-0 flex-1 rounded-lg bg-muted px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            type="submit"
            disabled={!draft.trim() || sending}
            className="shrink-0 rounded-lg bg-primary p-2 text-primary-foreground transition-transform disabled:opacity-40 active:scale-95"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      ) : (
        <div className="shrink-0 border-t border-border px-3 py-3 text-center text-xs text-muted-foreground">
          Log in to join the chat
        </div>
      )}
    </div>
  );
}

export { QUICK_REACTIONS };
